import React, { useState } from 'react';
import {
  Button,
  ButtonSize,
  ButtonVariant,
  CmhcIcon,
  CmhcIconSize,
  ToastNotification,
  ToastNotificationVariant
} from '@cmhc-schl/hearth-ui';
import { Box, Typography } from '@mui/material';
import { MemoizedHbtDataTableCell } from 'Feature/V2/Shared/MemoizedHbtDataTableCell';
import { useTranslation } from 'react-i18next';
import styles from '../styles.module.scss';
import { PropertyCharacteristicsSectionProps } from '../types';
import { TableRow } from './tableRow';
import { dwellingTypes, locationTypes, occupancyTypes, tenureTypes } from './utils/statusCodes';
import CopyContentToClipBoard from './CopyContentToClipBoard';

export const PropertyCharacteristicsSection = ({
  isCollapsed,
  onToggle,
  propertyData,
  className
}: PropertyCharacteristicsSectionProps) => {
  const { t } = useTranslation('HbtPortfolioManagementRequestOverview');
  const [showToaster, setShowToaster] = useState(false);
  const [toaster, setToasterMsg] = useState({
    icon: 'icon_check_circle',
    message: {
      header: t('Copied'),
      subHeader: String(' ')
    }
  });

  return (
    <>
      <Box className={styles.toasterNotification}>
        <ToastNotification
          dismissMode="autoAndActionable"
          variant={ToastNotificationVariant.INFO}
          open={showToaster}
          header={{ value: toaster.message.header }}
          subHeader={{ value: toaster.message.subHeader }}
          onClose={() => setShowToaster(false)}
          icon={toaster.icon}
        />
      </Box>

      <Box className={styles.accordionContainer}>
        <Typography variant="h6" className={styles.accordionTitle}>
          {t('Property-Characteristics')}
        </Typography>
        <Button
          type="button"
          className={styles.toggleButton}
          id={styles.toggleButton}
          name="lender"
          size={ButtonSize.SMALL}
          variant={ButtonVariant.SECONDARY}
          onClick={onToggle}
          leadingIcon={() => (
            <CmhcIcon
              className={styles.accordionIcon}
              size={CmhcIconSize.MEDIUM}
              type={isCollapsed ? 'icon_chevron_up' : 'icon_chevron_down'}
            />
          )}
          text={{
            value: ''
          }}
        />
      </Box>
      {!isCollapsed && (
        <Box className={styles.accordionBody}>
          <Box className={styles.tableContainer}>
            <TableRow
              leftTitle={t('Address')}
              leftValue={() => (
                <CopyContentToClipBoard
                  onSuccess={() => setShowToaster(true)}
                  showClipButton={Boolean(propertyData?.address)}
                  clipboardContent={propertyData?.address || ''}
                >
                  <MemoizedHbtDataTableCell
                    dataValue={String(propertyData?.address)}
                    cellContent={<span>{propertyData?.address}</span>}
                  />
                </CopyContentToClipBoard>
              )}
              rightTitle=""
              rightValue={() => <></>}
              className={styles.hideCellTopBorder}
            />
            <TableRow
              leftTitle={t('Legal-Description')}
              leftValue={() => (
                <CopyContentToClipBoard
                  onSuccess={() => setShowToaster(true)}
                  showClipButton={Boolean(propertyData?.legalDescription)}
                  clipboardContent={propertyData?.legalDescription || ''}
                >
                  <MemoizedHbtDataTableCell
                    dataValue={String(propertyData?.legalDescription)}
                    cellContent={<span>{propertyData?.legalDescription}</span>}
                  />
                </CopyContentToClipBoard>
              )}
              rightTitle={t('Location-Type')}
              rightValue={() => (
                <CopyContentToClipBoard
                  onSuccess={() => setShowToaster(true)}
                  showClipButton={Boolean(locationTypes(propertyData?.locationType || '', t))}
                  clipboardContent={locationTypes(propertyData?.locationType || '', t)}
                >
                  <MemoizedHbtDataTableCell
                    dataValue={String(propertyData?.locationType)}
                    cellContent={<span>{locationTypes(propertyData?.locationType || '', t)}</span>}
                  />
                </CopyContentToClipBoard>
              )}
            />
            <TableRow
              leftTitle={t('Occupancy-Type')}
              leftValue={() => (
                <CopyContentToClipBoard
                  onSuccess={() => setShowToaster(true)}
                  showClipButton={Boolean(occupancyTypes(propertyData?.occupancyType || '', t))}
                  clipboardContent={occupancyTypes(propertyData?.occupancyType || '', t)}
                >
                  <MemoizedHbtDataTableCell
                    dataValue={String(propertyData?.occupancyType)}
                    cellContent={
                      <span>{occupancyTypes(propertyData?.occupancyType || '', t)}</span>
                    }
                  />
                </CopyContentToClipBoard>
              )}
              rightTitle={t('No-Of-Units')}
              rightValue={() => (
                <CopyContentToClipBoard
                  onSuccess={() => setShowToaster(true)}
                  showClipButton={Boolean(propertyData?.noOfUnits)}
                  clipboardContent={propertyData?.noOfUnits || ''}
                >
                  <MemoizedHbtDataTableCell
                    dataValue={String(propertyData?.noOfUnits)}
                    cellContent={<span>{propertyData?.noOfUnits}</span>}
                  />
                </CopyContentToClipBoard>
              )}
            />
            <TableRow
              leftTitle={t('Dwelling-Type')}
              leftValue={() => (
                <CopyContentToClipBoard
                  onSuccess={() => setShowToaster(true)}
                  showClipButton={Boolean(dwellingTypes(propertyData?.dwellingType || '', t))}
                  clipboardContent={dwellingTypes(propertyData?.dwellingType || '', t)}
                >
                  <MemoizedHbtDataTableCell
                    dataValue={String(propertyData?.dwellingType)}
                    cellContent={<span>{dwellingTypes(propertyData?.dwellingType || '', t)}</span>}
                  />
                </CopyContentToClipBoard>
              )}
              rightTitle={t('Tenure-Type')}
              rightValue={() => (
                <CopyContentToClipBoard
                  onSuccess={() => setShowToaster(true)}
                  showClipButton={Boolean(tenureTypes(propertyData?.tenureType || '', t))}
                  clipboardContent={tenureTypes(propertyData?.tenureType || '', t)}
                >
                  <MemoizedHbtDataTableCell
                    dataValue={String(propertyData?.tenureType)}
                    cellContent={<span>{tenureTypes(propertyData?.tenureType || '', t)}</span>}
                  />
                </CopyContentToClipBoard>
              )}
            />
          </Box>
        </Box>
      )}
    </>
  );
};
